import {
  DotsThreeVertical,
  Download,
  PencilSimple,
  Plus,
  Trash,
} from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { AccentButtonOutline } from '../../../../../../../shared/Buttons';
import {
  Asset,
  CustomerWithDriverOnboarding,
  MaintenanceRecordWithDisplayName,
} from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { formatDate } from '../../../../../../../utils/DateUtils';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { MaintenanceModal } from '../maintenance/MaintenanceModal';
import toast from 'react-hot-toast';
import { isPreActiveCustomer } from '../../../home/utils/demoData';
import { useUser } from '../../../../../../../utils/UserContext';
import { capitalize } from '../../../../../../../utils/capitalize';

function MaintenanceRecords({
  addClass,
  maintenanceRecordsData,
  selectedAsset,
  loadingRecords,
  onRecordChange,
}: {
  addClass?: string;
  maintenanceRecordsData: MaintenanceRecordWithDisplayName[];
  selectedAsset: Asset;
  loadingRecords: boolean;
  onRecordChange: () => void;
}) {
  const { callProtectedApi } = useApi();
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const { screenView } = useScreenView();
  const [loadingDocMap, setLoadingDocMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const { userRecord } = useUser();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const [loadingDeleteMap, setLoadingDeleteMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [selectedMaintenanceRecord, setSelectedMaintenanceRecord] =
    useState<MaintenanceRecordWithDisplayName | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const togglePopup = (index: string | undefined) => {
    if (!index) return;
    setActivePopup(activePopup === index ? null : index);
  };

  const handleSetLoadingDoc = (loading: boolean, record_id: string) => {
    setLoadingDocMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(record_id, loading); // Toggle visibility for the item at index
      return newMap;
    });
  };

  const handleSetLoadingDelete = (loading: boolean, record_id: string) => {
    setLoadingDeleteMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(record_id, loading); // Toggle visibility for the item at index
      return newMap;
    });
  };

  const downloadMaintenanceRecordDocument = async (record_id: string) => {
    handleSetLoadingDoc(true, record_id);
    try {
      const response = await callProtectedApi(
        `/documents/display/maintenanceRecord/${record_id}`,
        'get',
        {},
        'Error occurred while downloading maintenance record document.'
      );
      const data = response?.data?.data?.presigned_url;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        window.open(data, '_blank');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while downloading maintenance record document: ',
        e
      );
    } finally {
      handleSetLoadingDoc(false, record_id);
    }
  };

  const deleteMaintenanceRecord = async (record_id: string) => {
    handleSetLoadingDelete(true, record_id);
    try {
      const response = await callProtectedApi(
        `/maintenanceRecords/delete/${record_id}`,
        'delete',
        {},
        'Error occurred while deleting maintenance record.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Maintenance record deleted successfully');
        onRecordChange();
      }
    } catch (e: any) {
      console.error('Error occurred while deleting maintenance record: ', e);
    } finally {
      handleSetLoadingDelete(false, record_id);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!activePopup) return;

    const clickedPopup = maintenanceRecordsData.find((record) => {
      const popupRef = popupRefs.current[record?.record_id ?? ''];
      return popupRef?.contains(event.target as Node);
    });

    if (!clickedPopup) {
      setActivePopup(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePopup]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <MaintenanceModal
              kind="record"
              mode="create"
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              selectedAsset={selectedAsset}
              onRecordChange={onRecordChange}
            />
            <MaintenanceModal
              kind="record"
              mode="edit"
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              selectedAsset={selectedAsset}
              onRecordChange={onRecordChange}
              existingRecord={
                selectedMaintenanceRecord ??
                ({} as MaintenanceRecordWithDisplayName)
              }
            />
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Maintenance Records
              </h2>
              {maintenanceRecordsData && maintenanceRecordsData?.length > 0 && (
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Record</h2>
                </AccentButtonOutline>
              )}
            </div>
            {maintenanceRecordsData && maintenanceRecordsData?.length > 0 ? (
              <div className="h-full w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Display Name
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Date
                      </th>
                      <th className="w-[90px] px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Created By
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        File(s)
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary"></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {maintenanceRecordsData.map((record, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-[14px] text-primary">
                          {capitalize(record?.maintenance_type)}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {record?.asset_display_name ||
                            record?.asset_display_name}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {formatDate(
                            new Date(record?.date_of_maintenance ?? ''),
                            true
                          )}
                        </td>
                        <td className="w-[90px] px-4 py-2 text-left text-[14px] text-primary">
                          {record?.is_system_generated ? 'System' : 'User'}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {record?.file_key ? (
                            <span
                              className={`flex items-center justify-start text-primary`}
                            >
                              {!loadingDocMap.get(record?.record_id) ? (
                                <Download
                                  onClick={() =>
                                    downloadMaintenanceRecordDocument(
                                      record?.record_id ?? ''
                                    )
                                  }
                                  className="mr-1 cursor-pointer text-primary"
                                  size={20}
                                />
                              ) : (
                                <ClipLoader
                                  color="#1D213B"
                                  loading={loadingDocMap.get(record?.record_id)}
                                  size={20}
                                  aria-label="Loading Spinner"
                                  data-testid="loader"
                                  className="h-full w-full justify-center"
                                />
                              )}
                              <p>{record?.file_key?.split('/').pop()}</p>
                            </span>
                          ) : (
                            <span className="text-graysecondary">None</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {!loadingDeleteMap.get(record?.record_id) ? (
                            <button
                              onClick={(e: any) => {
                                e.stopPropagation();
                                togglePopup(record?.record_id);
                              }}
                              className="relative"
                            >
                              <DotsThreeVertical size={26} weight="bold" />
                              {activePopup === record?.record_id && (
                                <div
                                  ref={(el) => {
                                    popupRefs.current[record?.record_id ?? ''] =
                                      el;
                                  }}
                                  className="absolute right-0 top-[1.5rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                                >
                                  <div>
                                    <button
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        setSelectedMaintenanceRecord(record);
                                        setIsEditModalOpen(true);
                                        togglePopup(record?.record_id ?? '');
                                      }}
                                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                    >
                                      <PencilSimple size={20} />
                                      <p>Edit</p>
                                    </button>
                                    <button
                                      onClick={async (e: any) => {
                                        e.stopPropagation();
                                        await deleteMaintenanceRecord(
                                          record?.record_id ?? ''
                                        );
                                        togglePopup(record?.record_id ?? '');
                                      }}
                                      className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                    >
                                      <Trash size={20} />
                                      <p>Remove Record</p>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </button>
                          ) : (
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingDeleteMap.get(record?.record_id)}
                              size={20}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="h-full w-full justify-center"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : loadingRecords ? (
              <ClipLoader
                color="#1D213B"
                loading={loadingRecords}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 h-full w-full justify-center"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-8 align-middle">
                <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
                  <h2 className="text-[16px] text-primary">
                    No Maintenance Records
                  </h2>
                  <p className="text-center text-[14px] text-graysecondary">
                    You have no maintenance records at the moment.
                  </p>
                </div>
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Record</h2>
                </AccentButtonOutline>
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <MaintenanceModal
              kind="record"
              mode="create"
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              selectedAsset={selectedAsset}
              onRecordChange={onRecordChange}
            />
            <MaintenanceModal
              kind="record"
              mode="edit"
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              selectedAsset={selectedAsset}
              onRecordChange={onRecordChange}
              existingRecord={
                selectedMaintenanceRecord ??
                ({} as MaintenanceRecordWithDisplayName)
              }
            />
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Maintenance Records
              </h2>
              {maintenanceRecordsData && maintenanceRecordsData?.length > 0 && (
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Record</h2>
                </AccentButtonOutline>
              )}
            </div>
            {maintenanceRecordsData && maintenanceRecordsData?.length > 0 ? (
              <div className="h-full w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Display Name
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Date
                      </th>
                      <th className="w-[90px] px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Created By
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        File(s)
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary"></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {maintenanceRecordsData.map((record, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-[14px] text-primary">
                          {capitalize(record?.maintenance_type)}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {record?.asset_display_name ||
                            record?.asset_display_name}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {formatDate(
                            new Date(record?.date_of_maintenance ?? ''),
                            true
                          )}
                        </td>
                        <td className="w-[90px] px-4 py-2 text-left text-[14px] text-primary">
                          {record?.is_system_generated ? 'System' : 'User'}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {record?.file_key ? (
                            <span
                              className={`flex items-center justify-start text-primary`}
                            >
                              {!loadingDocMap.get(record?.record_id) ? (
                                <Download
                                  onClick={() =>
                                    downloadMaintenanceRecordDocument(
                                      record?.record_id ?? ''
                                    )
                                  }
                                  className="mr-1 cursor-pointer text-primary"
                                  size={20}
                                />
                              ) : (
                                <ClipLoader
                                  color="#1D213B"
                                  loading={loadingDocMap.get(record?.record_id)}
                                  size={20}
                                  aria-label="Loading Spinner"
                                  data-testid="loader"
                                  className="h-full w-full justify-center"
                                />
                              )}
                              <p>{record?.file_key?.split('/').pop()}</p>
                            </span>
                          ) : (
                            <span className="text-graysecondary">None</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          <button
                            onClick={(e: any) => {
                              e.stopPropagation();
                              togglePopup(record?.record_id);
                            }}
                            className="relative"
                          >
                            <DotsThreeVertical size={26} weight="bold" />
                            {activePopup === record?.record_id && (
                              <div
                                ref={(el) => {
                                  popupRefs.current[record?.record_id ?? ''] =
                                    el;
                                }}
                                className="absolute right-0 top-[1.5rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                              >
                                <div>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      setSelectedMaintenanceRecord(record);
                                      setIsEditModalOpen(true);
                                      togglePopup(record?.record_id ?? '');
                                    }}
                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                  >
                                    <PencilSimple size={20} />
                                    <p>Edit</p>
                                  </button>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      togglePopup(record?.record_id ?? '');
                                    }}
                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                  >
                                    <Trash size={20} />
                                    <p>Remove Record</p>
                                  </button>
                                </div>
                              </div>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : loadingRecords ? (
              <ClipLoader
                color="#1D213B"
                loading={loadingRecords}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 h-full w-full justify-center"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-8 align-middle">
                <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
                  <h2 className="text-[16px] text-primary">
                    No Maintenance Records
                  </h2>
                  <p className="text-center text-[14px] text-graysecondary">
                    You have no maintenance records at the moment.
                  </p>
                </div>
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Record</h2>
                </AccentButtonOutline>
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <MaintenanceModal
              kind="record"
              mode="create"
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              selectedAsset={selectedAsset}
              onRecordChange={onRecordChange}
            />
            <MaintenanceModal
              kind="record"
              mode="edit"
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              selectedAsset={selectedAsset}
              onRecordChange={onRecordChange}
              existingRecord={
                selectedMaintenanceRecord ??
                ({} as MaintenanceRecordWithDisplayName)
              }
            />
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Maintenance Records
              </h2>
              {maintenanceRecordsData && maintenanceRecordsData?.length > 0 && (
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Record</h2>
                </AccentButtonOutline>
              )}
            </div>
            {maintenanceRecordsData && maintenanceRecordsData?.length > 0 ? (
              <div className="h-full w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Asset Display Name
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Date
                      </th>
                      <th className="w-[90px] px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Created By
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        File(s)
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary"></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {maintenanceRecordsData.map((record, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-[14px] text-primary">
                          {capitalize(record?.maintenance_type)}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {record?.asset_display_name ||
                            record?.asset_display_name}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {formatDate(
                            new Date(record?.date_of_maintenance ?? ''),
                            true
                          )}
                        </td>
                        <td className="w-[90px] px-4 py-2 text-left text-[14px] text-primary">
                          {record?.is_system_generated ? 'System' : 'User'}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          {record?.file_key ? (
                            <span
                              className={`flex items-center justify-start text-primary`}
                            >
                              {!loadingDocMap.get(record?.record_id) ? (
                                <Download
                                  onClick={() =>
                                    downloadMaintenanceRecordDocument(
                                      record?.record_id ?? ''
                                    )
                                  }
                                  className="mr-1 cursor-pointer text-primary"
                                  size={20}
                                />
                              ) : (
                                <ClipLoader
                                  color="#1D213B"
                                  loading={loadingDocMap.get(record?.record_id)}
                                  size={20}
                                  aria-label="Loading Spinner"
                                  data-testid="loader"
                                  className="h-full w-full justify-center"
                                />
                              )}
                              <p>{record?.file_key?.split('/').pop()}</p>
                            </span>
                          ) : (
                            <span className="text-graysecondary">None</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-left text-[14px] text-primary">
                          <button
                            onClick={(e: any) => {
                              e.stopPropagation();
                              togglePopup(record?.record_id);
                            }}
                            className="relative"
                          >
                            <DotsThreeVertical size={26} weight="bold" />
                            {activePopup === record?.record_id && (
                              <div
                                ref={(el) => {
                                  popupRefs.current[record?.record_id ?? ''] =
                                    el;
                                }}
                                className="absolute right-0 top-[1.5rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                              >
                                <div>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      setSelectedMaintenanceRecord(record);
                                      setIsEditModalOpen(true);
                                      togglePopup(record?.record_id ?? '');
                                    }}
                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                  >
                                    <PencilSimple size={20} />
                                    <p>Edit</p>
                                  </button>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      togglePopup(record?.record_id ?? '');
                                    }}
                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                  >
                                    <Trash size={20} />
                                    <p>Remove Record</p>
                                  </button>
                                </div>
                              </div>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : loadingRecords ? (
              <ClipLoader
                color="#1D213B"
                loading={loadingRecords}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 h-full w-full justify-center"
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-8 align-middle">
                <div className="flex flex-col items-center justify-center gap-y-1 align-middle">
                  <h2 className="text-[16px] text-primary">
                    No Maintenance Records
                  </h2>
                  <p className="text-center text-[14px] text-graysecondary">
                    You have no maintenance records at the moment.
                  </p>
                </div>
                <AccentButtonOutline
                  onClick={() => setIsModalOpen(true)}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <Plus size={20} />
                  <h2 className="text-[14px]">Add Record</h2>
                </AccentButtonOutline>
              </div>
            )}
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default MaintenanceRecords;
